import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// `(?)` icon with a hover/tap-revealed help bubble. Use next to a metric label
// or table heading to explain what the number means.
//
// The bubble renders in a body-level portal as a fixed-position element and
// clamps itself inside the viewport — so it never clips no matter where the icon
// sits or how small the screen is. `placement` ("top" | "bottom") is the
// preferred side and is auto-flipped when there isn't enough room.
// `iconClassName` overrides only the icon colour (e.g. white on a dark card).

const VIEWPORT_MARGIN = 8; // min gap kept between the bubble and the viewport edge
const ICON_GAP = 8;        // gap between the icon and the bubble
const DEFAULT_ICON_COLOR = "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300";

// Pass `children` to use any element as the hover trigger instead of the default
// `?` icon — e.g. wrap a KPI value so its formula reveals on hover.
const InfoTip = ({ text, placement = "top", iconClassName = DEFAULT_ICON_COLOR, children }) => {
  const triggerRef = useRef(null);
  const bubbleRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);

  const hide = useCallback(() => {
    setOpen(false);
    setCoords(null);
  }, []);

  // Once the bubble is in the DOM, centre it on the icon, clamp it inside the
  // viewport, and flip to the other side if the preferred one is too tight.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !bubbleRef.current) return;
    const t = triggerRef.current.getBoundingClientRect();
    const b = bubbleRef.current.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    const iconCenter = t.left + t.width / 2;

    let left = iconCenter - b.width / 2;
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - b.width - VIEWPORT_MARGIN));

    const roomAbove = t.top;
    const roomBelow = vh - t.bottom;
    let side = placement;
    if (side === "top" && roomAbove < b.height + ICON_GAP && roomBelow > roomAbove) side = "bottom";
    else if (side === "bottom" && roomBelow < b.height + ICON_GAP && roomAbove > roomBelow) side = "top";

    let top = side === "bottom" ? t.bottom + ICON_GAP : t.top - ICON_GAP - b.height;
    top = Math.max(VIEWPORT_MARGIN, Math.min(top, vh - b.height - VIEWPORT_MARGIN));

    setCoords({
      left,
      top,
      side,
      arrowLeft: Math.max(12, Math.min(iconCenter - left, b.width - 12)),
    });
  }, [open, placement, text]);

  // Dismiss on a tap or scroll elsewhere so the tip can't get stranded on touch.
  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target)) hide();
    };
    document.addEventListener("pointerdown", onOutside);
    window.addEventListener("scroll", hide, true);
    return () => {
      document.removeEventListener("pointerdown", onOutside);
      window.removeEventListener("scroll", hide, true);
    };
  }, [open, hide]);

  return (
    <span
      ref={triggerRef}
      className={children ? "relative inline-flex cursor-help" : "relative inline-flex ml-1.5"}
      onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
      onPointerEnter={(e) => { if (e.pointerType === "mouse") setOpen(true); }}
      onPointerLeave={(e) => { if (e.pointerType === "mouse") hide(); }}
      // React's onFocus/onBlur are focusin/focusout, so they fire for a focusable
      // `children` trigger (a link, a button) and give a keyboard reader the same reveal
      // a mouse gets. No effect on the default `?` icon, which is an unfocusable svg.
      onFocus={() => setOpen(true)}
      onBlur={hide}
    >
      {children ?? (
        <svg className={`w-3.5 h-3.5 cursor-help transition ${iconClassName}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      )}
      {open && createPortal(
        <div
          ref={bubbleRef}
          role="tooltip"
          style={{
            position: "fixed",
            left: coords ? coords.left : 0,
            top: coords ? coords.top : 0,
            zIndex: 9999,
            visibility: coords ? "visible" : "hidden",
            pointerEvents: "none",
          }}
          className="w-72 max-w-[calc(100vw-1rem)]"
        >
          <div className="relative bg-gray-900 text-white text-sm leading-relaxed rounded-lg px-4 py-3 shadow-2xl border border-white/20 whitespace-normal normal-case font-normal">
            {text}
            {coords && (
              <div
                className={`absolute border-[6px] border-transparent ${
                  coords.side === "bottom"
                    ? "bottom-full border-b-gray-900"
                    : "top-full border-t-gray-900"
                }`}
                style={{
                  left: coords.arrowLeft,
                  transform: "translateX(-50%)",
                  marginTop: coords.side === "bottom" ? 0 : -1,
                  marginBottom: coords.side === "bottom" ? -1 : 0,
                }}
              />
            )}
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
};

export default InfoTip;
